import { useState } from 'react';
import { FaEye, FaEyeSlash, FaCircleNotch } from 'react-icons/fa6';
import { useAuth } from '../../context/AuthContext';
import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { getUiText } from '../../lib/uiText';
import { isEnglishLocale, isSpanishLocale, ENGLISH_LOCALE } from '../../lib/locale';
import styles from './Login.module.css';

const Login = () => {
  const { login, error } = useAuth();
  const {
    companyInfo,
    footer,
    locale,
    changeLocale,
    spanishLocale,
    englishLocale,
    availableLocales,
  } = useGlobal();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isEn = isEnglishLocale(locale);
  const showLocaleSwitch = Array.isArray(availableLocales)
    && availableLocales.some((l) => isSpanishLocale(l))
    && availableLocales.includes(ENGLISH_LOCALE);

  const companyLogo = getStrapiMediaAttributes(companyInfo?.logoMain, {
    altFallback: companyInfo?.companyName || '',
    titleFallback: companyInfo?.companyName || '',
  });
  const creatorLogo = getStrapiMediaAttributes(footer?.creatorLogo);
  const creatorUrl = typeof footer?.creatorUrl === 'string' ? footer.creatorUrl.trim() : '';

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    await login(identifier, password);
    setSubmitting(false);
  };

  const toggleLocale = () => {
    changeLocale(isEn ? spanishLocale : englishLocale);
  };

  // Mensaje de error diferenciado: red vs credenciales.
  const errorMessage = error === 'NETWORK_ERROR'
    ? getUiText(locale, 'loginNetworkError')
    : getUiText(locale, 'loginError');

  return (
    <div className={styles.login}>
      <div className={styles.login__panel}>
        {showLocaleSwitch && (
          <button
            type="button"
            className={styles.login__localeSwitch}
            onClick={toggleLocale}
            aria-label={isEn ? 'Cambiar idioma a Español' : 'Switch language to English'}
          >
            {isEn ? 'Español' : 'English'}
          </button>
        )}

        <form className={styles.login__card} onSubmit={handleSubmit} noValidate>
          {companyLogo.src && (
            <div className={styles.login__logoBar}>
              <img
                src={companyLogo.src}
                alt={companyLogo.alt}
                title={companyLogo.title}
                className={styles.login__logo}
              />
            </div>
          )}

          <div className={styles.login__cardBody}>
            <h1 className={styles.login__title}>{getUiText(locale, 'loginTitle')}</h1>

            <label className={styles.login__field}>
              <span>{getUiText(locale, 'loginIdentifierLabel')}</span>
              <input
                type="text"
                name="identifier"
                autoComplete="username"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                required
              />
            </label>

            <label className={styles.login__field}>
              <span>{getUiText(locale, 'loginPasswordLabel')}</span>
              <div className={styles.login__passwordWrap}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
                <button
                  type="button"
                  className={styles.login__passwordToggle}
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={getUiText(locale, showPassword ? 'loginHidePassword' : 'loginShowPassword')}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </label>

            {error && (
              <p className={styles.login__error} role="alert">
                {errorMessage}
              </p>
            )}

            <button type="submit" className={styles.login__submit} disabled={submitting}>
              {submitting && (
                <FaCircleNotch className={styles.login__spinner} aria-hidden="true" />
              )}
              {submitting ? getUiText(locale, 'loginSubmitting') : getUiText(locale, 'loginSubmit')}
            </button>
          </div>
        </form>

        {creatorLogo.src && (
          <a
            href={creatorUrl || undefined}
            target={creatorUrl ? '_blank' : undefined}
            rel={creatorUrl ? 'noopener noreferrer' : undefined}
            className={styles.login__creator}
            title={footer?.creatorName || undefined}
          >
            <img src={creatorLogo.src} alt={creatorLogo.alt} title={creatorLogo.title} />
          </a>
        )}
      </div>
    </div>
  );
};

export default Login;
